import React, { useMemo } from 'react';
import { Clock, DollarSign, TrendingUp, Home, RefreshCw, BarChart2, BrainCircuit, AlertCircle } from 'lucide-react';
import { calculatePayment, calculateDaysToRenewal, calculateHELOC, getRenewalRecommendation } from '../../utils/calculations';
import PaywallGuard from '../PaywallGuard';
import PaymentShockCalculator from '../PaymentShockCalculator';

const OverviewTab = ({ user, mortgage, offers, marketRates, refreshRates }) => {
  const daysToRenewal = useMemo(() => calculateDaysToRenewal(mortgage.renewal_date), [mortgage.renewal_date]);
  
  const helocData = useMemo(() => {
    if (!mortgage.home_value || !mortgage.current_balance) return null;
    return calculateHELOC(mortgage.home_value, mortgage.current_balance, mortgage.primary_residence);
  }, [mortgage.home_value, mortgage.current_balance, mortgage.primary_residence]);
  
  const currentPayment = useMemo(() => {
    if (!mortgage.current_balance || !mortgage.current_rate || !mortgage.amortization_remain) {
      return { amount: 0, monthlyEquivalent: 0 };
    }
    return calculatePayment(
      mortgage.current_balance,
      mortgage.current_rate,
      mortgage.amortization_remain,
      mortgage.payment_frequency
    );
  }, [mortgage.current_balance, mortgage.current_rate, mortgage.amortization_remain, mortgage.payment_frequency]);
  
  const bestMarketRate = useMemo(() => {
    if (!marketRates.rates || marketRates.rates.length === 0) return 0;
    const rates = marketRates.rates.map(r => r.fixed_5y);
    return Math.min(...rates);
  }, [marketRates]);
  
  const renewalDecision = useMemo(() => {
    if (!mortgage.current_rate) return null;
    return getRenewalRecommendation(daysToRenewal, mortgage.current_rate, bestMarketRate);
  }, [daysToRenewal, mortgage.current_rate, bestMarketRate]);
  
  const bestOffer = useMemo(() => {
    if (offers.length === 0) return null;
    return offers.reduce((best, current) => 
      current.rate < best.rate ? current : best
    );
  }, [offers]);
  
  return (
    <div data-testid="overview-tab" className="space-y-6">
      {/* Payment Shock Calculator with Action Plan */}
      <PaymentShockCalculator mortgage={mortgage} marketRates={marketRates} user={user} />
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Days to Renewal</p>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <p className="text-3xl font-bold font-mono">{daysToRenewal}</p>
            <span className={`text-xs px-2 py-1 rounded mb-1 ${
              daysToRenewal <= 30 ? 'bg-red-500/20 text-red-400' :
              daysToRenewal <= 90 ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-green-500/20 text-green-400'
            }`}>
              {daysToRenewal <= 30 ? 'Urgent' : daysToRenewal <= 90 ? 'Soon' : 'On Track'}
            </span>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Balance</p>
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold font-mono">${(mortgage.current_balance || 0).toLocaleString('en-CA')}</p>
            <p className="text-xs text-muted-foreground mt-1">Rate: {mortgage.current_rate || 0}%</p>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Best Market Rate</p>
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold font-mono">{bestMarketRate.toFixed(2)}%</p>
            <p className="text-xs text-muted-foreground mt-1">5-Year Fixed</p>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Home className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Home Equity</p>
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold font-mono">
              ${helocData ? helocData.equity.toLocaleString('en-CA', {maximumFractionDigits: 0}) : '0'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              LTV: {helocData ? helocData.ltv.toFixed(1) : 0}%
            </p>
          </div>
        </div>
      </div>
      
      {/* Renewal Decision Engine */}
      <PaywallGuard feature="decision_engine" user={user} fallbackMessage="Upgrade to Premium or Pro to access the AI-powered decision engine.">
        {renewalDecision && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-heading font-semibold flex items-center gap-2 mb-4">
              <BrainCircuit className="h-5 w-5 text-primary" />
              Renewal Decision Engine
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-4 ${
                  renewalDecision.recommendation === 'RENEW NOW'
                    ? 'bg-primary/20 text-primary'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  <span className="text-2xl font-bold">{renewalDecision.recommendation}</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Confidence:</span>
                    <span className="font-medium">{renewalDecision.confidence}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Risk Level:</span>
                    <span className="font-medium">{renewalDecision.risk}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Action:</span>
                    <span className="font-medium">{renewalDecision.action}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold mb-2">Decision Reasoning:</h4>
                <ul className="space-y-1.5">
                  {renewalDecision.reasoning.map((reason, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </PaywallGuard>
      
      {/* Market Rates Summary */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-primary" />
            Market Rates Overview
          </h3>
          <PaywallGuard feature="rate_refresh" user={user} fallbackMessage="">
            <button
              onClick={refreshRates}
              data-testid="refresh-rates-btn"
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </PaywallGuard>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-background rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-2">Bank of Canada Policy Rate</p>
            <p className="text-2xl font-bold font-mono">{marketRates.boc_rate}%</p>
          </div>
          
          <div className="bg-background rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-2">Average 5Y Fixed Rate</p>
            <p className="text-2xl font-bold font-mono">
              {marketRates.rates && marketRates.rates.length > 0
                ? (marketRates.rates.reduce((sum, r) => sum + r.fixed_5y, 0) / marketRates.rates.length).toFixed(2)
                : '0.00'
              }%
            </p>
          </div>
        </div>
      </div>
      
      {/* Best Offer */}
      {bestOffer && (
        <div className="bg-card border border-primary/50 rounded-xl p-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Your Best Offer</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold">{bestOffer.lender}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {bestOffer.rate}% • {bestOffer.term}-year {bestOffer.type}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Potential Savings</p>
              <p className="text-2xl font-bold text-primary">
                {mortgage.current_rate > bestOffer.rate
                  ? `${(mortgage.current_rate - bestOffer.rate).toFixed(2)}%`
                  : '0%'
                }
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {!mortgage.current_balance && (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Mortgage Data Yet</h3>
          <p className="text-muted-foreground mb-4">
            Get started by entering your mortgage details in the "Mortgage Details" tab.
          </p>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;